import { useEffect, useState } from "react";

const THEME_STORAGE_KEY = "taller-theme";

const objetivos = [
  "Modelar un personaje en porcelana fría desde sus formas básicas.",
  "Trabajar estructura, detalles, expresividad y terminaciones.",
  "Desarrollar identidad visual y presencia del personaje.",
  "Construir historia, personalidad y universo desde la escritura.",
  "Explorar movimiento y puesta en escena a través de la animación.",
];

const programa = [
  {
    clase: "Sábado 11/4 · Primera clase",
    descripcion:
      "Modelado del personaje en porcelana fría. Introducción a formas básicas y estructura.",
    emoji: "✨",
  },
  {
    clase: "Sábado 18/4 · Segunda clase",
    descripcion:
      "Continuación del modelado con foco en detalles, expresividad y terminaciones.",
    emoji: "🎨",
  },
  {
    clase: "Sábado 24/4 · Tercera clase",
    descripcion:
      "Creación del personaje a partir de la escritura: historia, personalidad y universo.",
    emoji: "📝",
  },
  {
    clase: "Sábado 9/5 · Cuarta clase",
    descripcion:
      "Animación del personaje. Exploración del movimiento y puesta en escena.",
    emoji: "🎬",
  },
];

const materiales = [
  "Montevideo",
  "4 encuentros",
  "Sábados",
  "14:30 a 17:30 hs",
];

const horarios = [
  {
    titulo: "Ubicación",
    hora: "Montevideo",
    icono: "📍",
    detalle: "Encuentros presenciales en Montevideo.",
  },
  {
    titulo: "Duración",
    hora: "4 encuentros",
    icono: "🗓️",
    detalle: "Un recorrido breve y concentrado en cuatro clases.",
  },
  {
    titulo: "Días",
    hora: "Sábados",
    icono: "📆",
    detalle: "Una frecuencia semanal para sostener el proceso entre clases.",
  },
  {
    titulo: "Horario",
    hora: "14:30 a 17:30 hs",
    icono: "🕝",
    detalle: "Tres horas por encuentro para desarrollar el trabajo en taller.",
  },
];

const heroHighlights = [
  { etiqueta: "Ciudad", valor: "Montevideo", icono: "📍" },
  {
    etiqueta: "Duración",
    valor: "4 encuentros",
    icono: "🗓️",
  },
  { etiqueta: "Inicio", valor: "Sábado 11/4", icono: "📅" },
];

const creativeRoute = [
  {
    titulo: "Modelar",
    descripcion:
      "Primera y segunda clase: modelado del personaje, formas básicas, estructura, detalles y terminaciones.",
    tono:
      "from-rose-200/80 to-orange-100/70 dark:from-rose-900/35 dark:to-orange-900/10",
  },
  {
    titulo: "Escribir",
    descripcion:
      "Tercera clase: creación del personaje desde la escritura para desarrollar historia, personalidad y universo.",
    tono:
      "from-cyan-200/80 to-sky-100/70 dark:from-cyan-900/35 dark:to-sky-900/10",
  },
  {
    titulo: "Animar",
    descripcion:
      "Cuarta clase: exploración del movimiento y puesta en escena del personaje.",
    tono:
      "from-amber-200/80 to-yellow-100/70 dark:from-amber-900/35 dark:to-yellow-900/10",
  },
];

const summaryItems = [
  { titulo: "Ubicación", valor: "Montevideo" },
  { titulo: "Duración", valor: "4 encuentros" },
  { titulo: "Días", valor: "Sábados" },
  { titulo: "Horario", valor: "14:30 a 17:30 hs" },
  { titulo: "Inicio", valor: "Sábado 11/4" },
];

function getObjetivoCardClassName(index) {
  const styles = [
    "border-rose-200 bg-rose-50/80 dark:border-rose-900/60 dark:bg-rose-950/35",
    "border-cyan-200 bg-cyan-50/80 dark:border-cyan-900/60 dark:bg-cyan-950/30",
    "border-amber-200 bg-amber-50/90 dark:border-amber-900/60 dark:bg-amber-950/30",
    "border-violet-200 bg-violet-50/80 dark:border-violet-900/60 dark:bg-violet-950/30",
  ];

  return `relative overflow-hidden rounded-[1.75rem] border p-5 shadow-lg shadow-rose-100/50 backdrop-blur-md transition-transform duration-300 hover:-translate-y-1 dark:shadow-black/10 ${styles[index % styles.length]}`;
}

function getProgramaCardClassName(index) {
  const styles = [
    "before:bg-rose-300/90 dark:before:bg-rose-500/80",
    "before:bg-cyan-300/90 dark:before:bg-cyan-500/80",
    "before:bg-amber-300/90 dark:before:bg-amber-500/80",
    "before:bg-violet-300/90 dark:before:bg-violet-500/80",
  ];

  return `relative overflow-hidden rounded-[1.9rem] border border-white/70 bg-white/80 p-6 shadow-[0_26px_80px_rgba(255,255,255,0.24)] backdrop-blur-lg before:absolute before:bottom-0 before:left-0 before:top-0 before:w-2 before:content-[''] dark:border-white/10 dark:bg-slate-950/60 dark:shadow-black/20 ${styles[index % styles.length]}`;
}

function runDevChecks() {
  const sampleClasses = [
    getObjetivoCardClassName(0),
    getObjetivoCardClassName(1),
    getObjetivoCardClassName(2),
    getObjetivoCardClassName(3),
    getObjetivoCardClassName(4),
  ];

  console.assert(
    sampleClasses[0].includes("border-rose-200 bg-rose-50/80"),
    "Test failed: style for index 0 should be rose",
  );
  console.assert(
    sampleClasses[1].includes("border-cyan-200 bg-cyan-50/80"),
    "Test failed: style for index 1 should be cyan",
  );
  console.assert(
    sampleClasses[2].includes("border-amber-200 bg-amber-50/90"),
    "Test failed: style for index 2 should be amber",
  );
  console.assert(
    sampleClasses[3].includes("border-violet-200 bg-violet-50/80"),
    "Test failed: style for index 3 should be violet",
  );
  console.assert(
    sampleClasses[4] === sampleClasses[0],
    "Test failed: styles should wrap around after index 3",
  );
}

runDevChecks();

function getPreferredTheme() {
  if (typeof window === "undefined") {
    return "light";
  }

  const savedTheme = window.localStorage.getItem(THEME_STORAGE_KEY);

  if (savedTheme === "dark" || savedTheme === "light") {
    return savedTheme;
  }

  return window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
}

export default function TallerPorcelanaFriaWeb() {
  const [theme, setTheme] = useState(getPreferredTheme);
  const toggleLabel =
    theme === "dark" ? "Cambiar a modo claro" : "Cambiar a modo oscuro";

  useEffect(() => {
    const rootElement = document.documentElement;

    rootElement.classList.toggle("dark", theme === "dark");
    window.localStorage.setItem(THEME_STORAGE_KEY, theme);
  }, [theme]);

  return (
    <div className="relative isolate min-h-screen overflow-hidden bg-[linear-gradient(180deg,#fff7f1,#ffece4_38%,#fff5ef_100%)] text-slate-800 dark:bg-[linear-gradient(180deg,#130f18,#19131f_38%,#100d14_100%)] dark:text-slate-100">
      <div className="pointer-events-none absolute inset-0 opacity-40 [background-image:radial-gradient(rgba(255,255,255,0.85)_1px,transparent_1px)] [background-position:0_0] [background-size:18px_18px] dark:opacity-10" />
      <div className="pointer-events-none absolute inset-x-0 top-0 z-0 h-[42rem] bg-[radial-gradient(circle_at_10%_18%,rgba(255,171,170,0.28),transparent_24%),radial-gradient(circle_at_88%_10%,rgba(101,205,255,0.2),transparent_21%),radial-gradient(circle_at_70%_42%,rgba(255,219,126,0.18),transparent_24%)] dark:bg-[radial-gradient(circle_at_10%_18%,rgba(244,114,182,0.14),transparent_24%),radial-gradient(circle_at_88%_10%,rgba(34,211,238,0.12),transparent_21%),radial-gradient(circle_at_70%_42%,rgba(245,158,11,0.1),transparent_24%)]" />

      <header
        className="relative isolate overflow-hidden border-b border-white/50 dark:border-white/10"
        style={{
          backgroundImage: "url('/bg.png')",
          backgroundPosition: "center",
          backgroundSize: "cover",
        }}
      >
        <div className="absolute inset-0 bg-[linear-gradient(135deg,rgba(255,245,239,0.96),rgba(255,240,248,0.82)_42%,rgba(234,249,255,0.58)),radial-gradient(circle_at_top_left,rgba(255,255,255,0.5),transparent_32%)] dark:bg-[linear-gradient(135deg,rgba(10,10,15,0.9),rgba(19,15,24,0.84)_42%,rgba(16,26,38,0.74)),radial-gradient(circle_at_top_left,rgba(255,255,255,0.06),transparent_28%)]" />
        <div className="absolute inset-0 backdrop-blur-[3px]" />
        <div className="absolute -left-10 top-24 h-36 w-36 rounded-full bg-rose-200/35 blur-3xl dark:bg-rose-500/10" />
        <div className="absolute right-6 top-14 h-44 w-44 rounded-full bg-cyan-200/35 blur-3xl dark:bg-cyan-500/10" />
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-40 bg-gradient-to-b from-transparent to-[rgba(255,247,241,0.96)] dark:to-[rgba(19,15,24,0.95)]" />

        <div className="relative z-10 mx-auto max-w-7xl px-6 pb-16 pt-8 lg:px-8 lg:pb-20 lg:pt-10">
          <div className="flex items-start justify-end">
            <button
              type="button"
              aria-label={toggleLabel}
              aria-pressed={theme === "dark"}
              className="inline-flex items-center gap-3 rounded-full border border-white/70 bg-white/90 px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm backdrop-blur-md transition-transform duration-300 hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400 focus-visible:ring-offset-2 dark:border-white/10 dark:bg-slate-900/80 dark:text-slate-100 dark:focus-visible:ring-offset-slate-950"
              onClick={() => {
                setTheme((currentTheme) =>
                  currentTheme === "dark" ? "light" : "dark",
                );
              }}
            >
              <span aria-hidden="true" className="text-base">
                {theme === "dark" ? "☀️" : "🌙"}
              </span>
              <span>{theme === "dark" ? "Modo claro" : "Modo oscuro"}</span>
            </button>
          </div>

          <div className="mt-8 grid gap-8 xl:grid-cols-[1.08fr_0.92fr] xl:items-start">
            <section className="relative overflow-hidden rounded-[2rem] border border-white/65 bg-white/60 px-7 py-8 shadow-[0_28px_80px_rgba(152,89,113,0.16)] backdrop-blur-xl dark:border-white/10 dark:bg-slate-950/60 dark:shadow-black/30 sm:px-9 sm:py-10">
              <div className="pointer-events-none absolute inset-x-0 left-[-10%] top-0 h-44 bg-[radial-gradient(circle,rgba(255,255,255,0.32),transparent_58%)] dark:bg-[radial-gradient(circle,rgba(255,255,255,0.08),transparent_58%)]" />
              <div className="relative z-10">
                <span className="inline-flex items-center rounded-full border border-rose-200/70 bg-white/90 px-4 py-2 text-sm font-semibold text-rose-700 shadow-sm backdrop-blur-sm dark:border-rose-500/20 dark:bg-slate-900/70 dark:text-rose-200">
                  Taller creativo · Modelado + escritura + animación
                </span>

                <h1 className="mt-6 max-w-4xl font-serif text-5xl leading-[0.95] tracking-[-0.04em] text-slate-900 sm:text-6xl lg:text-7xl dark:text-white">
                  Taller de Creación de Personajes
                </h1>

                <p className="mt-6 max-w-3xl text-lg leading-8 text-slate-700 dark:text-slate-300">
                  Un taller en Montevideo para modelar, escribir y animar un
                  personaje propio en porcelana fría a lo largo de cuatro
                  encuentros.
                </p>

                <p className="mt-4 max-w-3xl text-base leading-7 text-slate-600 dark:text-slate-400">
                  El recorrido comienza con el modelado, sigue con el desarrollo
                  de historia y personalidad, y cierra con una exploración del
                  movimiento y la puesta en escena.
                </p>

                <div className="mt-8 grid gap-4 sm:grid-cols-3">
                  {heroHighlights.map((item) => (
                    <div
                      key={item.etiqueta}
                      className="relative min-h-[8.75rem] overflow-hidden rounded-[1.75rem] border border-white/70 bg-white/80 p-5 shadow-lg shadow-rose-100/50 backdrop-blur-md transition-transform duration-300 hover:-translate-y-1 dark:border-white/10 dark:bg-slate-950/55 dark:shadow-black/10"
                    >
                      <p className="text-2xl">{item.icono}</p>
                      <p className="mt-4 text-xs font-black uppercase tracking-[0.24em] text-slate-500 dark:text-slate-400">
                        {item.etiqueta}
                      </p>
                      <p className="mt-2 text-lg font-extrabold leading-7 text-slate-900 dark:text-white">
                        {item.valor}
                      </p>
                    </div>
                  ))}
                </div>

                <div className="mt-8 flex flex-wrap gap-3 text-sm font-semibold text-slate-600 dark:text-slate-300">
                  <span className="inline-flex items-center rounded-full border border-white/70 bg-white/80 px-4 py-2 shadow-sm backdrop-blur-sm dark:border-white/10 dark:bg-slate-900/70">
                    4 encuentros
                  </span>
                  <span className="inline-flex items-center rounded-full border border-white/70 bg-white/80 px-4 py-2 shadow-sm backdrop-blur-sm dark:border-white/10 dark:bg-slate-900/70">
                    Sábados
                  </span>
                  <span className="inline-flex items-center rounded-full border border-white/70 bg-white/80 px-4 py-2 shadow-sm backdrop-blur-sm dark:border-white/10 dark:bg-slate-900/70">
                    14:30 a 17:30 hs
                  </span>
                </div>
              </div>
            </section>

            <aside className="relative overflow-hidden rounded-[2rem] border border-white/70 bg-white/80 px-6 py-6 shadow-[0_28px_80px_rgba(152,89,113,0.14)] backdrop-blur-lg dark:border-white/10 dark:bg-slate-950/60 dark:shadow-black/25 sm:px-7">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(255,255,255,0.24),transparent_30%)] dark:bg-[radial-gradient(circle_at_top_right,rgba(255,255,255,0.06),transparent_24%)]" />
              <div className="relative z-10">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="text-xs font-black uppercase tracking-[0.32em] text-rose-500 dark:text-rose-300">
                      Cronograma
                    </p>
                    <h2 className="mt-2 font-serif text-3xl tracking-[-0.03em] text-slate-900 dark:text-white">
                      Cuatro encuentros para crear un personaje
                    </h2>
                  </div>
                  <div className="rounded-full border border-white/70 bg-white/75 px-4 py-2 text-sm font-black text-slate-700 shadow-sm backdrop-blur dark:border-white/10 dark:bg-slate-900/70 dark:text-slate-100">
                    4 clases
                  </div>
                </div>

                <div className="mt-6 space-y-4">
                  {creativeRoute.map((item, index) => (
                    <article
                      key={item.titulo}
                      className={`relative overflow-hidden rounded-[1.75rem] border border-white/60 bg-gradient-to-br ${item.tono} p-5 shadow-lg shadow-white/30 backdrop-blur-md dark:border-white/10 dark:shadow-black/10`}
                    >
                      <div className="absolute -right-8 -top-8 h-24 w-24 rounded-full bg-white/35 blur-2xl dark:bg-white/10" />
                      <div className="relative">
                        <div className="flex items-center gap-3">
                          <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white/90 text-sm font-black text-slate-800 shadow-sm dark:bg-slate-900/70 dark:text-white">
                            0{index + 1}
                          </div>
                          <h3 className="text-xl font-extrabold text-slate-900 dark:text-white">
                            {item.titulo}
                          </h3>
                        </div>
                        <p className="mt-4 max-w-md leading-7 text-slate-700 dark:text-slate-200">
                          {item.descripcion}
                        </p>
                      </div>
                    </article>
                  ))}
                </div>

                <div className="mt-6 grid gap-4 sm:grid-cols-2">
                  {horarios.map((horario) => (
                    <div
                      key={horario.titulo}
                      className="rounded-[1.6rem] border border-white/70 bg-white/70 px-5 py-5 shadow-sm backdrop-blur-md dark:border-white/10 dark:bg-slate-950/55"
                    >
                      <p className="text-2xl">{horario.icono}</p>
                      <p className="mt-3 text-xs font-black uppercase tracking-[0.24em] text-slate-500 dark:text-slate-400">
                        {horario.titulo}
                      </p>
                      <p className="mt-2 text-lg font-extrabold text-slate-900 dark:text-white">
                        {horario.hora}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </aside>
          </div>
        </div>
      </header>

      <main className="relative z-10 mx-auto max-w-7xl px-6 pb-20 pt-10 lg:px-8 lg:pb-24">
        <section className="grid gap-8 lg:grid-cols-[1.05fr_0.95fr]">
          <article className="relative overflow-hidden rounded-[2rem] border border-white/70 bg-white/80 px-7 py-8 shadow-[0_28px_80px_rgba(152,89,113,0.12)] backdrop-blur-lg dark:border-white/10 dark:bg-slate-950/60 dark:shadow-black/25">
            <p className="text-xs font-black uppercase tracking-[0.32em] text-rose-500 dark:text-rose-300">
              Sobre El Taller
            </p>
            <h2 className="mt-3 font-serif text-4xl tracking-[-0.03em] text-slate-900 dark:text-white">
              Un recorrido breve para crear un personaje completo
            </h2>
            <p className="mt-5 max-w-2xl text-lg leading-8 text-slate-700 dark:text-slate-300">
              El taller propone cuatro encuentros presenciales en Montevideo,
              los sábados de 14:30 a 17:30 hs, con un recorrido que pasa por el
              modelado, la escritura y la animación.
            </p>
            <p className="mt-4 max-w-2xl leading-8 text-slate-600 dark:text-slate-400">
              Cada clase trabaja una etapa distinta: construir la forma del
              personaje, definir su expresividad, desarrollar su universo y
              explorar cómo se mueve en escena.
            </p>

            <div className="mt-8 grid gap-4 sm:grid-cols-3">
              <div className="relative overflow-hidden rounded-[1.75rem] border border-rose-200/80 bg-rose-50/90 p-5 shadow-lg shadow-rose-100/50 backdrop-blur-md transition-transform duration-300 hover:-translate-y-1 dark:border-rose-900/60 dark:bg-rose-950/30 dark:shadow-black/10">
                <p className="text-xs font-black uppercase tracking-[0.24em] text-rose-500 dark:text-rose-300">
                  Lugar
                </p>
                <p className="mt-3 text-lg font-extrabold text-slate-900 dark:text-white">
                  Montevideo
                </p>
              </div>
              <div className="relative overflow-hidden rounded-[1.75rem] border border-cyan-200/80 bg-cyan-50/90 p-5 shadow-lg shadow-rose-100/50 backdrop-blur-md transition-transform duration-300 hover:-translate-y-1 dark:border-cyan-900/60 dark:bg-cyan-950/30 dark:shadow-black/10">
                <p className="text-xs font-black uppercase tracking-[0.24em] text-cyan-600 dark:text-cyan-300">
                  Modalidad
                </p>
                <p className="mt-3 text-lg font-extrabold text-slate-900 dark:text-white">
                  4 encuentros
                </p>
              </div>
              <div className="relative overflow-hidden rounded-[1.75rem] border border-amber-200/80 bg-amber-50/90 p-5 shadow-lg shadow-rose-100/50 backdrop-blur-md transition-transform duration-300 hover:-translate-y-1 dark:border-amber-900/60 dark:bg-amber-950/30 dark:shadow-black/10">
                <p className="text-xs font-black uppercase tracking-[0.24em] text-amber-600 dark:text-amber-300">
                  Horario
                </p>
                <p className="mt-3 text-lg font-extrabold text-slate-900 dark:text-white">
                  Sábados, 14:30 a 17:30 hs
                </p>
              </div>
            </div>
          </article>

          <article className="relative overflow-hidden rounded-[2rem] border border-white/70 bg-white/80 px-7 py-8 shadow-[0_28px_80px_rgba(152,89,113,0.12)] backdrop-blur-lg dark:border-white/10 dark:bg-slate-950/60 dark:shadow-black/25">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-xs font-black uppercase tracking-[0.32em] text-rose-500 dark:text-rose-300">
                  Datos Del Taller
                </p>
                <h2 className="mt-3 font-serif text-4xl tracking-[-0.03em] text-slate-900 dark:text-white">
                  Cuándo y cómo se organiza
                </h2>
              </div>
              <div className="rounded-full bg-slate-900 px-4 py-2 text-sm font-bold text-white dark:bg-white dark:text-slate-900">
                Abril y mayo
              </div>
            </div>

            <div className="mt-6 grid gap-4">
              {horarios.map((horario) => (
                <div
                  key={horario.titulo}
                  className="flex flex-col gap-4 rounded-[1.75rem] border border-white/70 bg-white/80 p-5 shadow-sm backdrop-blur-md dark:border-white/10 dark:bg-slate-950/55 sm:flex-row sm:items-center sm:justify-between"
                >
                  <div className="flex items-center gap-4">
                    <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-violet-100 text-2xl dark:bg-violet-950/45">
                      {horario.icono}
                    </div>
                    <div>
                      <p className="text-xs font-black uppercase tracking-[0.24em] text-violet-500 dark:text-violet-300">
                        {horario.titulo}
                      </p>
                      <p className="mt-2 text-xl font-extrabold text-slate-900 dark:text-white">
                        {horario.hora}
                      </p>
                    </div>
                  </div>
                  <p className="max-w-xs text-sm leading-6 text-slate-600 dark:text-slate-400">
                    {horario.detalle}
                  </p>
                </div>
              ))}
            </div>
          </article>
        </section>

        <section className="mt-12 grid gap-8 xl:grid-cols-[0.92fr_1.08fr]">
          <article className="relative overflow-hidden rounded-[2rem] border border-white/70 bg-white/80 px-7 py-8 shadow-[0_28px_80px_rgba(152,89,113,0.12)] backdrop-blur-lg dark:border-white/10 dark:bg-slate-950/60 dark:shadow-black/25">
            <p className="text-xs font-black uppercase tracking-[0.32em] text-rose-500 dark:text-rose-300">
              Objetivos
            </p>
            <h2 className="mt-3 font-serif text-4xl tracking-[-0.03em] text-slate-900 dark:text-white">
              Lo que se trabaja durante el proceso
            </h2>

            <div className="mt-7 grid gap-4">
              {objetivos.map((objetivo, index) => (
                <div key={objetivo} className={getObjetivoCardClassName(index)}>
                  <div className="flex items-start gap-4">
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-white/80 text-sm font-black text-slate-800 shadow-sm dark:bg-slate-900/70 dark:text-white">
                      0{index + 1}
                    </span>
                    <p className="text-base font-semibold leading-7 text-slate-700 dark:text-slate-200">
                      {objetivo}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </article>

          <article className="relative overflow-hidden rounded-[2.2rem] bg-[linear-gradient(135deg,#ff7fae,#ff9d76_52%,#ffd36d)] px-7 py-8 text-white shadow-[0_30px_100px_rgba(232,117,129,0.35)] dark:bg-[linear-gradient(135deg,#6f2444,#8d3d22_52%,#6f5321)]">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(255,255,255,0.28),transparent_30%),radial-gradient(circle_at_bottom_left,rgba(255,255,255,0.12),transparent_32%)]" />
            <div className="relative">
              <p className="text-xs font-black uppercase tracking-[0.28em] text-white/75">
                Resumen Rápido
              </p>
              <h2 className="mt-3 max-w-xl font-serif text-4xl tracking-[-0.03em] text-white sm:text-5xl">
                Todo lo importante, de un vistazo.
              </h2>
              <p className="mt-4 max-w-xl text-base leading-7 text-white/90">
                La información principal del taller queda resumida en formato
                breve para consultar fechas, horario y modalidad rápidamente.
              </p>

              <div className="mt-8 grid gap-4 sm:grid-cols-2">
                {summaryItems.map((item) => (
                  <div
                    key={item.titulo}
                    className="rounded-[1.6rem] border border-white/30 bg-white/14 px-5 py-5 backdrop-blur-sm"
                  >
                    <p className="text-xs font-black uppercase tracking-[0.24em] text-white/70">
                      {item.titulo}
                    </p>
                    <p className="mt-2 text-lg font-extrabold leading-7 text-white">
                      {item.valor}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </article>
        </section>

        <section className="mt-14">
          <div className="max-w-3xl">
            <p className="text-xs font-black uppercase tracking-[0.32em] text-rose-500 dark:text-rose-300">
              Cronograma
            </p>
            <h2 className="mt-3 font-serif text-4xl tracking-[-0.03em] text-slate-900 dark:text-white sm:text-5xl">
              Cuatro encuentros para crear tu personaje
            </h2>
            <p className="mt-4 text-lg leading-8 text-slate-600 dark:text-slate-400">
              El cronograma avanza desde el modelado en porcelana fría hacia la
              escritura del universo del personaje y su exploración en
              movimiento.
            </p>
          </div>

          <div className="mt-8 grid gap-5 lg:grid-cols-2">
            {programa.map((item, index) => (
              <article key={item.clase} className={getProgramaCardClassName(index)}>
                <div className="relative z-10 flex gap-4">
                  <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-white/90 text-2xl shadow-sm dark:bg-slate-900/75">
                    {item.emoji}
                  </div>
                  <div>
                    <div className="inline-flex rounded-full border border-slate-200/80 bg-slate-50/90 px-3 py-1 text-xs font-black uppercase tracking-[0.2em] text-slate-500 dark:border-slate-700 dark:bg-slate-900/60 dark:text-slate-300">
                      Clase {index + 1}
                    </div>
                    <h3 className="mt-4 text-xl font-extrabold leading-8 text-slate-900 dark:text-white">
                      {item.clase}
                    </h3>
                    <p className="mt-3 leading-7 text-slate-600 dark:text-slate-300">
                      {item.descripcion}
                    </p>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </section>

        <section className="mt-14 grid gap-8 lg:grid-cols-[1.04fr_0.96fr]">
          <article className="relative overflow-hidden rounded-[2rem] border border-white/70 bg-white/80 px-7 py-8 shadow-[0_28px_80px_rgba(152,89,113,0.12)] backdrop-blur-lg dark:border-white/10 dark:bg-slate-950/60 dark:shadow-black/25">
            <p className="text-xs font-black uppercase tracking-[0.32em] text-rose-500 dark:text-rose-300">
              Información Práctica
            </p>
            <h2 className="mt-3 font-serif text-4xl tracking-[-0.03em] text-slate-900 dark:text-white">
              Datos clave del taller
            </h2>

            <ul className="mt-7 grid gap-4">
              {materiales.map((material, index) => (
                <li
                  key={material}
                  className="relative flex gap-4 overflow-hidden rounded-[1.75rem] border border-white/70 bg-white/80 p-5 shadow-lg shadow-rose-100/50 backdrop-blur-md transition-transform duration-300 hover:-translate-y-1 dark:border-white/10 dark:bg-slate-950/55 dark:shadow-black/10"
                >
                  <span className="mt-0.5 text-2xl">
                    {index === 0
                      ? "🧁"
                      : index === 1
                        ? "🖌️"
                        : index === 2
                          ? "🧵"
                          : "🧰"}
                  </span>
                  <span className="leading-7 text-slate-700 dark:text-slate-300">
                    {material}
                  </span>
                </li>
              ))}
            </ul>
          </article>

          <article className="relative overflow-hidden rounded-[2rem] border border-white/70 bg-white/80 px-7 py-8 shadow-[0_28px_80px_rgba(152,89,113,0.12)] backdrop-blur-lg dark:border-white/10 dark:bg-slate-950/60 dark:shadow-black/25">
            <p className="text-xs font-black uppercase tracking-[0.32em] text-rose-500 dark:text-rose-300">
              Recorrido Del Taller
            </p>
            <h2 className="mt-3 font-serif text-4xl tracking-[-0.03em] text-slate-900 dark:text-white">
              Tres focos para construir el personaje
            </h2>
            <p className="mt-5 leading-8 text-slate-700 dark:text-slate-300">
              El proceso reúne trabajo manual, desarrollo narrativo y una
              instancia final de exploración escénica para darle presencia al
              personaje.
            </p>
            <div className="mt-7 grid gap-4">
              <div className="relative overflow-hidden rounded-[1.75rem] border border-rose-200/80 bg-rose-50/90 p-5 shadow-lg shadow-rose-100/50 backdrop-blur-md transition-transform duration-300 hover:-translate-y-1 dark:border-rose-900/60 dark:bg-rose-950/30 dark:shadow-black/10">
                <p className="text-xs font-black uppercase tracking-[0.24em] text-rose-500 dark:text-rose-300">
                  Modelado
                </p>
                <p className="mt-2 text-lg font-extrabold text-slate-900 dark:text-white">
                  Formas básicas, estructura y trabajo de terminación
                </p>
              </div>
              <div className="relative overflow-hidden rounded-[1.75rem] border border-cyan-200/80 bg-cyan-50/90 p-5 shadow-lg shadow-rose-100/50 backdrop-blur-md transition-transform duration-300 hover:-translate-y-1 dark:border-cyan-900/60 dark:bg-cyan-950/30 dark:shadow-black/10">
                <p className="text-xs font-black uppercase tracking-[0.24em] text-cyan-600 dark:text-cyan-300">
                  Escritura
                </p>
                <p className="mt-2 text-lg font-extrabold text-slate-900 dark:text-white">
                  Historia, personalidad y universo del personaje
                </p>
              </div>
              <div className="relative overflow-hidden rounded-[1.75rem] border border-amber-200/80 bg-amber-50/90 p-5 shadow-lg shadow-rose-100/50 backdrop-blur-md transition-transform duration-300 hover:-translate-y-1 dark:border-amber-900/60 dark:bg-amber-950/30 dark:shadow-black/10">
                <p className="text-xs font-black uppercase tracking-[0.24em] text-amber-600 dark:text-amber-300">
                  Animación
                </p>
                <p className="mt-2 text-lg font-extrabold text-slate-900 dark:text-white">
                  Exploración del movimiento y puesta en escena
                </p>
              </div>
            </div>
          </article>
        </section>
      </main>

      <footer className="relative z-10 pb-8 pt-2 lg:pb-10">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="relative overflow-hidden rounded-[2.25rem] border border-white/70 bg-[linear-gradient(135deg,rgba(255,248,243,0.95),rgba(255,238,245,0.88)_42%,rgba(238,248,255,0.82))] shadow-[0_28px_90px_rgba(152,89,113,0.14)] backdrop-blur-xl dark:border-white/10 dark:bg-[linear-gradient(135deg,rgba(19,15,24,0.94),rgba(34,19,28,0.9)_45%,rgba(16,26,38,0.84))] dark:shadow-black/25">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_12%_18%,rgba(255,188,186,0.42),transparent_24%),radial-gradient(circle_at_88%_26%,rgba(101,205,255,0.26),transparent_22%),radial-gradient(circle_at_74%_88%,rgba(255,217,120,0.24),transparent_26%)] dark:bg-[radial-gradient(circle_at_12%_18%,rgba(244,114,182,0.16),transparent_24%),radial-gradient(circle_at_88%_26%,rgba(34,211,238,0.14),transparent_22%),radial-gradient(circle_at_74%_88%,rgba(245,158,11,0.12),transparent_26%)]" />
            <div className="absolute -left-10 top-6 h-28 w-28 rounded-full bg-white/45 blur-3xl dark:bg-white/5" />
            <div className="absolute bottom-0 right-0 h-36 w-36 translate-x-8 translate-y-8 rounded-full bg-rose-200/35 blur-3xl dark:bg-rose-500/10" />

            <div className="relative grid gap-8 px-6 py-8 sm:px-8 lg:grid-cols-[1.15fr_0.85fr] lg:px-10 lg:py-10">
              <div className="max-w-2xl">
                <p className="text-xs font-black uppercase tracking-[0.32em] text-rose-500 dark:text-rose-300">
                  Comunidad
                </p>
                <h2 className="mt-3 font-serif text-3xl tracking-[-0.03em] text-slate-900 dark:text-white sm:text-4xl">
                  Seguinos para ver nuevas piezas, procesos y próximas fechas
                  del taller.
                </h2>
                <p className="mt-4 max-w-xl text-base leading-7 text-slate-600 dark:text-slate-300">
                  En Instagram compartimos trabajos recientes, novedades del
                  taller y escenas del universo de Estatuitas Bang Bang.
                </p>
              </div>

              <div className="flex items-center lg:justify-end">
                <div className="relative w-full max-w-sm overflow-hidden rounded-[1.9rem] border border-white/75 bg-white/68 p-5 shadow-lg shadow-rose-100/50 backdrop-blur-md dark:border-white/10 dark:bg-slate-950/55 dark:shadow-black/15">
                  <div className="absolute inset-x-0 top-0 h-1 bg-[linear-gradient(90deg,#f97316,#ec4899,#38bdf8)]" />
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <p className="text-xs font-black uppercase tracking-[0.24em] text-slate-500 dark:text-slate-400">
                        Instagram
                      </p>
                      <p className="mt-2 text-lg font-extrabold text-slate-900 dark:text-white">
                        @estatuitasbangbang
                      </p>
                    </div>
                    <a
                      href="https://www.instagram.com/estatuitasbangbang/"
                      target="_blank"
                      rel="noreferrer"
                      aria-label="Instagram @estatuitasbangbang"
                      className="inline-flex h-16 w-16 shrink-0 items-center justify-center rounded-full border border-rose-200/80 bg-white/90 p-2 shadow-[0_10px_35px_rgba(236,72,153,0.22)] transition-transform duration-300 hover:-translate-y-1 hover:scale-[1.03] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rose-400 focus-visible:ring-offset-2 dark:border-rose-500/25 dark:bg-slate-900/90 dark:shadow-black/20 dark:focus-visible:ring-offset-slate-950"
                    >
                      <img
                        src="/instagram-icon.svg"
                        alt=""
                        aria-hidden="true"
                        className="h-10 w-10"
                      />
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
